<template>
  <div class="wrraper driver-details-admin-page">
    <!-- Start Breadcrumb -->
    <div class="mb-3">
      <Breadcrumb :items="items" />
    </div>
    <!-- End Breadcrumb -->

    <transition-group name="fadeInUp" mode="in-out">
      <!-- Start Main Loader -->
      <MainLoader key="loader" v-if="loading" />
      <!-- End Main Loader -->

      <div key="main" v-else class="main-store-details">
        <!-- Top Modern Floating Header Bar -->
        <div class="modern-card store-hero-card mb-4" v-if="allData">
          <div
            class="d-flex flex-wrap justify-content-between align-items-center gap-3"
          >
            <!-- Store ID & Status Summary -->
            <div class="d-flex align-items-center flex-wrap gap-3">
              <div class="store-id-badge">
                <i class="far fa-store ml-2 text-primary"></i>
                <span>تفاصيل المتجر: </span>
                <strong class="store-num">#{{ allData.id }}</strong>
              </div>

              <!-- Qualification Status Dot & Label -->
              <div
                class="qualification-status-wrapper"
                @mouseenter="showTermsTooltip = true"
                @mouseleave="showTermsTooltip = false"
              >
                <span
                  class="status-dot"
                  :class="
                    allData.is_fully_qualified
                      ? 'status-dot--green'
                      : 'status-dot--gray'
                  "
                ></span>
                <span class="status-dot-label">
                  {{ allData.is_fully_qualified ? "مؤهل بالكامل" : "غير مؤهل" }}
                </span>

                <!-- Terms Tooltip -->
                <transition name="terms-pop">
                  <div
                    class="terms-tooltip"
                    v-if="
                      showTermsTooltip && allData.terms && allData.terms.length
                    "
                  >
                    <div class="terms-tooltip__header">
                      <i class="fas fa-clipboard-list"></i>
                      <span>الشروط المطلوبة</span>
                    </div>
                    <ul class="terms-tooltip__list">
                      <li
                        v-for="(term, index) in allData.terms"
                        :key="index"
                        class="terms-tooltip__item"
                      >
                        <span class="terms-tooltip__index">{{
                          index + 1
                        }}</span>
                        <span>{{ term.term_trans }}</span>
                        <p>
                          <v-chip
                            small
                            :color="term.status ? 'success' : 'error'"
                          >
                            {{ term.status ? "نعم" : "لا" }}
                          </v-chip>
                        </p>
                      </li>
                    </ul>
                  </div>
                </transition>
              </div>

              <!-- Active / Ban Status Badges -->
              <span
                class="badge badge-pill font-weight-bold px-3 py-1"
                :class="
                  allData.is_ban
                    ? 'bg-danger text-white'
                    : 'bg-success text-white'
                "
              >
                {{ allData.is_ban ? "محظور" : "نشط" }}
              </span>
            </div>

            <!-- Header Control Action Buttons -->
            <div class="d-flex flex-wrap align-items-center gap-2">
              <!-- Store Chat Button -->
              <v-btn
                depressed
                color="amber darken-1"
                class="custom-hero-btn text-white rounded-pill px-4"
                @click="isChatOpen = true"
              >
                <i class="far fa-comments ml-2"></i>
                محادثة المتجر
              </v-btn>

              <!-- Edit Store Button -->
              <v-btn
                depressed
                color="#783d73"
                class="custom-hero-btn text-white rounded-pill px-4"
                :to="`/store/edit/${allData.id}`"
              >
                <i class="far fa-edit ml-2"></i>
                تعديل المتجر
              </v-btn>

              <!-- Balance Transfer Button -->
              <v-btn
                depressed
                color="success"
                class="custom-hero-btn text-white rounded-pill px-4"
                @click="isChangeBalanceModel = true"
              >
                <i class="far fa-wallet ml-2"></i>
                تحويل رصيد
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Main Content 2-Column Grid -->
        <div class="row align-items-start" v-if="allData">
          <!-- Left Sidebar Column (3 Cols) -->
          <div class="col-lg-3 mb-4">
            <div class="modern-card store-sidebar-card p-3">
              <!-- Store Image Avatar Ring -->
              <div class="store_image_container text-center mb-3">
                <div class="avatar-glow-ring">
                  <img
                    :src="allData.store.store_image"
                    v-if="allData.store.store_image"
                    alt="Store Image"
                  />
                  <img
                    :src="require('@/assets/images/icons/logo.png')"
                    v-else
                    alt="Fallback Logo"
                  />
                </div>
              </div>

              <!-- Store Name & Category -->
              <h5 class="text-center font-weight-bold text-dark mb-2">
                {{ allData.store ? allData.store.trad_name : "" }}
              </h5>

              <div class="text-center mb-3" v-if="allData.place_category">
                <span class="store-category-chip">
                  <i class="far fa-tag ml-1"></i>
                  {{ allData.place_category.name }}
                </span>
              </div>

              <!-- Website Link -->
              <div class="text-center mb-3" v-if="allData.website_url">
                <a
                  :href="allData.website_url"
                  target="_blank"
                  class="website-link-pill"
                  rel="noopener noreferrer"
                >
                  <i class="fas fa-globe"></i>
                  <span>زيارة الموقع الإلكتروني</span>
                </a>
              </div>

              <!-- Navigation Vertical Tabs -->
              <ul class="store_taps_container mb-4">
                <li class="store_tap">
                  <button
                    @click="active = 'info'"
                    :class="active == 'info' ? 'active' : ''"
                  >
                    <i class="far fa-info-circle ml-2"></i>
                    <span>بيانات المتجر</span>
                  </button>
                </li>
                <li class="store_tap">
                  <button
                    @click="active = 'menu'"
                    :class="active == 'menu' ? 'active' : ''"
                  >
                    <i class="far fa-utensils ml-2"></i>
                    <span> الوجبات والمنيو</span>
                  </button>
                </li>
                <li class="store_tap">
                  <button
                    @click="active = 'branches'"
                    :class="
                      active == 'branches' || active == 'addBranch'
                        ? 'active'
                        : ''
                    "
                  >
                    <i class="far fa-code-branch ml-2"></i>
                    <span>فروع المتجر</span>
                  </button>
                </li>
                <li class="store_tap">
                  <button
                    @click="active = 'store_images'"
                    :class="active == 'store_images' ? 'active' : ''"
                  >
                    <i class="far fa-images ml-2"></i>
                    <span>معرض صور المتجر</span>
                  </button>
                </li>
              </ul>

              <!-- Account Control Switches -->
              <div class="switches-box p-3 bg-light rounded-lg mb-3">
                <div
                  class="custom-switch-row d-flex justify-content-between align-items-center p-2 rounded-lg mb-2"
                  @click="toggleacceptModel(allData)"
                  role="button"
                  title="تغيير حالة قبول المتجر"
                >
                  <div class="d-flex align-items-center gap-2">
                    <span class="font-weight-bold text-dark small"
                      >بيانات المتجر مقبولة؟</span
                    >
                    <span
                      class="badge badge-pill font-weight-bold px-2 py-0"
                      :class="
                        allData.is_admin_active_user
                          ? 'badge-light-success'
                          : 'badge-light-secondary'
                      "
                      style="font-size: 11px"
                    >
                      {{ allData.is_admin_active_user ? "مقبول" : "غير مقبول" }}
                    </span>
                  </div>
                  <div class="switch-wrapper" style="pointer-events: none">
                    <v-switch
                      :input-value="Boolean(allData.is_admin_active_user)"
                      color="success"
                      readonly
                      dense
                      hide-details
                      class="mt-0 pt-0"
                    />
                  </div>
                </div>

                <div
                  class="custom-switch-row d-flex justify-content-between align-items-center p-2 rounded-lg"
                  @click="toggleStatusModel(allData)"
                  role="button"
                  title="تغيير حالة حظر المتجر"
                >
                  <div class="d-flex align-items-center gap-2">
                    <span class="font-weight-bold text-dark small"
                      >حظر المتجر</span
                    >
                    <span
                      class="badge badge-pill font-weight-bold px-2 py-0"
                      :class="
                        allData.is_ban
                          ? 'badge-light-danger'
                          : 'badge-light-success'
                      "
                      style="font-size: 11px"
                    >
                      {{ allData.is_ban ? "محظور" : "نشط" }}
                    </span>
                  </div>
                  <div class="switch-wrapper" style="pointer-events: none">
                    <v-switch
                      :input-value="Boolean(allData.is_ban)"
                      color="error"
                      readonly
                      dense
                      hide-details
                      class="mt-0 pt-0"
                    />
                  </div>
                </div>

                <!-- Ban Reason Box -->
                <div
                  class="ban-reason-alert mt-3 p-2 bg-light-danger border-danger rounded"
                  v-if="allData.ban_reason"
                >
                  <h6 class="font-weight-bold text-danger mb-1 small">
                    <i class="far fa-exclamation-triangle ml-1"></i>سبب الحظر:
                  </h6>
                  <div class="small text-muted">{{ allData.ban_reason }}</div>
                </div>
              </div>

              <!-- Working Hours Box -->
              <div
                class="working-hours-box p-3 bg-light rounded-lg mb-3"
                v-if="allData.store && allData.store.working_times"
              >
                <h6 class="font-weight-bold text-primary mb-2 small">
                  <i class="far fa-clock ml-1"></i>أوقات وأيام العمل
                </h6>
                <div
                  class="d-flex justify-content-between small text-muted mb-1"
                >
                  <span>أيام العمل:</span>
                  <span class="font-weight-bold text-dark">
                    {{ allData.store.working_times.day_from_trans }} -
                    {{ allData.store.working_times.day_to_trans }}
                  </span>
                </div>
                <div class="d-flex justify-content-between small text-muted">
                  <span>ساعات العمل:</span>
                  <span class="font-weight-bold text-dark">
                    {{ allData.store.working_times.from }} -
                    {{ allData.store.working_times.to }}
                  </span>
                </div>
              </div>

              <!-- Wallet Balance Card Widget -->
              <div class="wallet-widget-card p-3 rounded-lg text-white">
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <span class="small font-weight-bold">
                    <i class="far fa-wallet ml-1"></i>محفظة المتجر
                  </span>
                  <button
                    class="wallet-action-btn"
                    @click="isChangeBalanceModel = true"
                    title="تحويل رصيد"
                  >
                    <i class="far fa-plus-circle"></i>
                  </button>
                </div>
                <h4 class="font-weight-bold mb-0">
                  {{ allData.wallet ? allData.wallet.toFixed(2) : "0.00" }}
                </h4>
              </div>
            </div>
          </div>

          <!-- Right Content Area (9 Cols) -->
          <div class="col-lg-9 py-0">
            <Transition name="routerTransition" mode="out-in">
              <StoreInfo v-if="active == 'info'" :data="allData" />
              <Menu v-if="active == 'menu'" />
              <StoreBranches
                :data="allData"
                v-else-if="active == 'branches'"
                @addBranch="active = 'addBranch'"
                @editBranch="eidtBranch"
                @get_store="setRows"
                @showBranch="infoBranch"
              />
              <AddBranch
                :id="allData.id"
                v-else-if="active == 'addBranch'"
                @branchAdded="resetStore"
                :clear="clear_fileds"
              />
              <EditBranch
                v-else-if="active == 'editBranch'"
                :id="allData.id"
                @branchAdded="resetStore"
              />
              <BranchInfo
                v-else-if="active == 'baranch_info'"
                :id="branchId"
                :data="myData"
              />

              <!-- Store Images Gallery Card -->
              <div
                v-else-if="active == 'store_images'"
                class="store_gallery_wrapper p-4 p-md-5 mb-5"
              >
                <div
                  class="gallery_header mb-4 d-flex align-items-center justify-content-between"
                >
                  <div
                    class="header_title_group d-flex align-items-center gap-3"
                  >
                    <div class="header_icon_box">
                      <i class="fal fa-images"></i>
                    </div>
                    <div>
                      <h5 class="m-0 font-weight-bold">
                        معرض صور المتجر والوثائق الرسمية
                      </h5>
                      <span class="text-muted text_small"
                        >معاينة السجل التجاري والشهادات المرفقة للمتجر</span
                      >
                    </div>
                  </div>
                </div>

                <div class="row g-4" v-if="allData && allData.store">
                  <!-- 1. Store Main Image -->
                  <div class="col-xl-6 col-lg-6 col-md-6 col-12 mb-4">
                    <div class="creative_gallery_card">
                      <div class="card_tag_header">
                        <i class="fal fa-store"></i>
                        <span>صورة المتجر الرئيسية</span>
                      </div>
                      <div
                        class="gallery_img_container"
                        @click="
                          openImageModal(
                            allData.store.store_image,
                            'صورة المتجر الرئيسية'
                          )
                        "
                      >
                        <img
                          v-if="allData.store.store_image"
                          :src="allData.store.store_image"
                          alt="Store Image"
                          class="gallery_img"
                        />
                        <div
                          class="zoom_hover_overlay"
                          v-if="allData.store.store_image"
                        >
                          <div class="zoom_icon_box">
                            <i class="fal fa-search-plus"></i>
                          </div>
                          <span>انقر لتكبير المعاينة</span>
                        </div>
                        <div class="empty_image_placeholder" v-else>
                          <i class="fal fa-image-slash"></i>
                          <span>لم يتم إرفاق صورة</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 2. Store Outside View -->
                  <div class="col-xl-6 col-lg-6 col-md-6 col-12 mb-4">
                    <div class="creative_gallery_card">
                      <div class="card_tag_header">
                        <i class="fal fa-store-alt"></i>
                        <span>المتجر من الخارج</span>
                      </div>
                      <div
                        class="gallery_img_container"
                        @click="
                          openImageModal(
                            allData.store.restaurant_picture_from_outside,
                            'المتجر من الخارج'
                          )
                        "
                      >
                        <img
                          v-if="allData.store.restaurant_picture_from_outside"
                          :src="allData.store.restaurant_picture_from_outside"
                          alt="Store Outside"
                          class="gallery_img"
                        />
                        <div
                          class="zoom_hover_overlay"
                          v-if="allData.store.restaurant_picture_from_outside"
                        >
                          <div class="zoom_icon_box">
                            <i class="fal fa-search-plus"></i>
                          </div>
                          <span>انقر لتكبير المعاينة</span>
                        </div>
                        <div class="empty_image_placeholder" v-else>
                          <i class="fal fa-image-slash"></i>
                          <span>لم يتم إرفاق صورة</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 3. Commercial Registration -->
                  <div class="col-xl-6 col-lg-6 col-md-6 col-12 mb-4">
                    <div class="creative_gallery_card">
                      <div class="card_tag_header">
                        <i class="fal fa-file-contract"></i>
                        <span>السجل التجاري</span>
                      </div>
                      <div
                        class="gallery_img_container"
                        @click="
                          openImageModal(
                            allData.store.commercial_registration,
                            'السجل التجاري'
                          )
                        "
                      >
                        <img
                          v-if="allData.store.commercial_registration"
                          :src="allData.store.commercial_registration"
                          alt="Commercial Registration"
                          class="gallery_img"
                        />
                        <div
                          class="zoom_hover_overlay"
                          v-if="allData.store.commercial_registration"
                        >
                          <div class="zoom_icon_box">
                            <i class="fal fa-search-plus"></i>
                          </div>
                          <span>انقر لتكبير المعاينة</span>
                        </div>
                        <div class="empty_image_placeholder" v-else>
                          <i class="fal fa-image-slash"></i>
                          <span>لم يتم إرفاق صورة</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 4. VAT Certificate -->
                  <div class="col-xl-6 col-lg-6 col-md-6 col-12 mb-4">
                    <div class="creative_gallery_card">
                      <div class="card_tag_header">
                        <i class="fal fa-file-invoice-dollar"></i>
                        <span>الضريبة المضافة</span>
                      </div>
                      <div
                        class="gallery_img_container"
                        @click="
                          openImageModal(
                            allData.store.vat_picture,
                            'الضريبة المضافة'
                          )
                        "
                      >
                        <img
                          v-if="allData.store.vat_picture"
                          :src="allData.store.vat_picture"
                          alt="VAT Certificate"
                          class="gallery_img"
                        />
                        <div
                          class="zoom_hover_overlay"
                          v-if="allData.store.vat_picture"
                        >
                          <div class="zoom_icon_box">
                            <i class="fal fa-search-plus"></i>
                          </div>
                          <span>انقر لتكبير المعاينة</span>
                        </div>
                        <div class="empty_image_placeholder" v-else>
                          <i class="fal fa-image-slash"></i>
                          <span>لم يتم إرفاق صورة</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </transition-group>

    <!-- Dialogs & Modals -->

    <!-- 1. Balance Transfer Dialog -->
    <v-dialog v-model="isChangeBalanceModel" width="420">
      <v-card class="modern-dialog-card p-4">
        <form @submit.prevent="toggleBalance">
          <h5 class="main_color text-center font-weight-bold mb-3">
            تحويل رصيد
          </h5>
          <div class="input_wrapper top_label mb-3">
            <label class="form-label"> نوع التحويل </label>
            <multiselect
              v-model="transaction.type"
              :options="types"
              label="name"
              track-by="name"
              placeholder=" "
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-4">
            <input
              step="any"
              type="number"
              class="form-control custom-input-field"
              v-model="transaction.amount"
              @input="helper_checkIfInputIsEmpty"
            />
            <label class="form-label"> المبلغ </label>
          </div>
          <div class="text-center">
            <v-btn
              depressed
              color="success"
              class="px-10 rounded-pill text-white"
              :loading="balanceLoading"
              type="submit"
            >
              تحويل
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- 2. Ban / Unban Store Dialog -->
    <v-dialog v-model="statusModel" width="460">
      <v-card class="modern-dialog-card p-4" v-if="allData">
        <h5 class="text-center font-weight-bold mb-3" v-if="allData.is_ban">
          هل تود فك حظر هذا المتجر؟
        </h5>
        <h5 class="text-center font-weight-bold mb-3" v-else>
          هل تود حظر هذا المتجر؟
        </h5>

        <textarea
          v-if="!allData.is_ban"
          v-model="deactive_reason"
          placeholder="أدخل سبب الحظر..."
          class="form-control custom-textarea-field mb-4"
          rows="3"
        ></textarea>
        <div class="d-flex justify-content-center gap-3">
          <v-btn
            depressed
            class="px-8 rounded-pill text-white"
            @click="changeStatus"
            :loading="statusLoading"
            :color="!allData.is_ban ? 'error' : 'success'"
          >
            نعم
          </v-btn>
          <v-btn
            depressed
            class="px-8 rounded-pill"
            @click="statusModel = false"
          >
            لا
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- 3. Accept Store Details Dialog -->
    <v-dialog v-model="acceptModel" width="460">
      <v-card class="modern-dialog-card p-4" v-if="allData">
        <h5
          class="text-center font-weight-bold mb-4"
          v-if="!allData.is_admin_active_user"
        >
          هل تود قبول بيانات هذا المتجر؟
        </h5>
        <h5 class="text-center font-weight-bold mb-4" v-else>
          هل تود إلغاء قبول بيانات هذا المتجر؟
        </h5>

        <div class="d-flex justify-content-center gap-3">
          <v-btn
            depressed
            class="px-8 rounded-pill text-white"
            @click="changeAcceptStatus"
            :color="allData.is_admin_active_user ? 'error' : 'success'"
            :loading="acceptLoading"
          >
            نعم
          </v-btn>
          <v-btn
            depressed
            class="px-8 rounded-pill"
            @click="acceptModel = false"
          >
            لا
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Base Image Preview Modal -->
    <v-dialog
      v-model="model_1.show_model"
      max-width="850"
      content-class="creative_lightbox_dialog_overlay"
    >
      <v-card
        class="lightbox_card_wrapper text-center p-4 p-md-5"
        v-if="model_1.model_img_src"
      >
        <!-- Close Button -->
        <button
          class="lightbox_close_btn"
          @click="model_1.show_model = false"
          title="إغلاق المعاينة"
        >
          <i class="fal fa-times"></i>
        </button>

        <!-- Header -->
        <div class="lightbox_header mb-4">
          <div class="header_badge">
            <i class="fal fa-eye"></i>
            <span>معاينة مكبرة</span>
          </div>
          <h4 class="lightbox_title mt-2 mb-0" v-if="model_1.model_title">
            {{ model_1.model_title }}
          </h4>
        </div>

        <!-- Image Display Stage -->
        <div class="lightbox_stage mb-4">
          <div class="stage_glow_bg"></div>
          <img
            :src="model_1.model_img_src"
            alt="Preview"
            class="lightbox_image"
          />
        </div>

        <!-- Action Buttons Footer -->
        <div
          class="lightbox_footer_actions d-flex align-items-center justify-content-center gap-3"
        >
          <a
            :href="model_1.model_img_src"
            target="_blank"
            class="btn_lightbox_primary"
          >
            <i class="fal fa-external-link-alt"></i>
            <span>فتح بالحجم الكامل</span>
          </a>

          <a
            :href="model_1.model_img_src"
            download
            class="btn_lightbox_secondary"
          >
            <i class="fal fa-download"></i>
            <span>تحميل الصورة</span>
          </a>

          <button
            type="button"
            class="btn_lightbox_close"
            @click="model_1.show_model = false"
          >
            <span>إغلاق</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Floating Chat Window -->
    <FloatingChat
      v-if="isChatOpen"
      :userId="id"
      :userType="'restaurant'"
      :userName="allData ? (allData.store ? allData.store.trad_name : '') : ''"
      :userAvatar="
        allData ? (allData.store ? allData.store.store_image : '') : ''
      "
      @close="isChatOpen = false"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import imageFalse from "@/assets/images/icons/logo.png";
import StoreInfo from "./showComponents/storeInfo.vue";
import StoreBranches from "./showComponents/storeBranches.vue";
import AddBranch from "./showComponents/addNewBranch.vue";
import EditBranch from "./showComponents/editBranch.vue";
import BranchInfo from "./showComponents/BranchInfo.vue";
import Menu from "./showComponents/Menu.vue";
import FloatingChat from "@/components/Chat/FloatingChat.vue";

export default {
  props: ["id"],
  name: "Withdraw",

  components: {
    StoreInfo,
    StoreBranches,
    AddBranch,
    EditBranch,
    BranchInfo,
    Menu,
    FloatingChat,
  },

  data() {
    return {
      isChatOpen: false,
      showTermsTooltip: false,
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.stores.title"),
          href: "/stores/show-all",
        },
      ],

      statisticsItem: {},
      allData: null,
      active: this.$route.query.page || "info",
      loading: false,
      clear_fileds: false,
      model_1: {
        show_model: false,
        model_img_src: "",
        model_title: "",
      },
      statusModel: false,
      deactive_reason: null,
      statusLoading: false,
      acceptLoading: false,
      acceptModel: false,
      isChangeBalanceModel: false,
      balanceLoading: false,
      branchId: null,
      myData: null,
      transaction: {
        type: null,
        amount: null,
      },
      types: [
        {
          id: "withdraw",
          name: "سحب",
        },
        {
          id: "charge",
          name: "ايداع",
        },
      ],
      itemToEdit: null,
    };
  },

  watch: {
    active(value) {
      if (value == "addBranch") {
        this.clear_fileds = !this.clear_fileds;
      } else if (value == "baranch_info") {
        this.$router.push({ query: { page: value, id: this.branchId } });
      } else {
        this.$router.push({ query: { page: value } });
      }
    },
    ["$route.query.page"](value) {
      this.active = value;
    },
    statusModel() {
      this.deactive_reason = null;
    },
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),
  },

  methods: {
    eidtBranch(item) {
      localStorage.setItem("zoz_store_branch", JSON.stringify(item));
      this.active = "editBranch";
    },
    toggleBalance() {
      this.balanceLoading = true;
      if (!this.transaction.type) {
        this.$iziToast.error({
          timeout: 2000,
          message: "ادخل نوع التحويل",
          position: "bottomCenter",
        });
        this.balanceLoading = false;
        return;
      }
      if (!this.transaction.amount) {
        this.balanceLoading = false;
        return;
      }
      const formDta = new FormData();
      formDta.append("amount", this.transaction.amount);

      this.$axios
        .post(`${this.transaction.type.id}/${this.allData.id}`, formDta)
        .then((res) => {
          this.balanceLoading = false;
          this.isChangeBalanceModel = false;
          this.transaction.amount = null;
          this.transaction.type = null;
          this.$iziToast.success({
            timeout: 2000,
            message: res.data.message,
            position: "bottomCenter",
          });
          this.setRows();
        })
        .catch((err) => {
          this.balanceLoading = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || "فشل التحويل",
            position: "bottomCenter",
          });
        });
    },
    openImageModal(src, title) {
      if (!src) return;
      this.model_1.model_img_src = src;
      this.model_1.model_title = title || "معاينة الصورة";
      this.model_1.show_model = true;
    },
    show_model_1(e) {
      this.model_1.model_img_src = e.target?.src || e;
      this.model_1.model_title = "معاينة الصورة";
      this.model_1.show_model = true;
    },
    toggleBan() {
      this.banLoading = true;
      this.$axios.get(`drivers/${this.allData.id}/ban`).then(() => {
        this.banLoading = false;
        this.setRows();
      });
    },
    toggleacceptModel() {
      this.acceptModel = true;
    },
    changeAcceptStatus() {
      this.acceptLoading = true;
      const formData = new FormData();

      formData.append(
        "is_admin_active_user",
        this.allData.is_admin_active_user ? 0 : 1
      );
      formData.append("_method", "PUT");

      this.$axios({
        method: "POST",
        url: `/store/adminStatus/${this.allData.id}`,
        data: formData,
      })
        .then((res) => {
          this.$iziToast.success({
            timeout: 5000,
            message: res.data.message,
            position: "bottomRight",
          });
          this.acceptModel = false;
          this.acceptLoading = false;
          this.setRows();
        })
        .catch(() => {
          this.loading = false;
          this.acceptLoading = false;
        });
    },
    toggleStatusModel() {
      this.statusModel = true;
    },
    changeStatus() {
      if (!this.allData.is_ban && !this.deactive_reason) {
        this.$iziToast.error({
          message: "يرجي ادخال سبب الحظر اولا",
          timeout: 5000,
          position: "bottomRight",
        });
        return;
      }
      this.statusLoading = true;
      const formData = new FormData();
      formData.append("is_active", this.allData.is_ban ? 0 : 1);
      if (this.deactive_reason) {
        formData.append("message", this.deactive_reason);
      }
      formData.append("_method", "PUT");

      this.$axios({
        method: "POST",
        url: `/store/is_active/${this.allData.id}`,
        data: formData,
      })
        .then((res) => {
          this.$iziToast.success({
            timeout: 5000,
            message: res.data.message,
            position: "bottomRight",
          });
          this.statusModel = false;
          this.setRows();
          this.statusLoading = false;
        })
        .catch(() => {
          this.loading = false;
          this.statusLoading = false;
        });
    },
    replaceImage(e) {
      e.target.src = imageFalse;
    },
    resetStore() {
      this.active = "branches";
      this.setRows();
    },
    infoBranch(item) {
      this.active = "baranch_info";
      this.branchRows(item.id);
      this.branchId = item.id;
    },
    setRows() {
      this.loading = true;
      this.$axios({
        method: "GET",
        url: "store/" + this.id,
      })
        .then((res) => {
          this.allData = res.data.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    branchRows(id) {
      this.loading = true;
      this.$axios({
        method: "GET",
        url: "store/" + id,
      })
        .then((res) => {
          this.myData = res.data.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },

  created() {
    this.setRows();
  },
};
</script>

<style lang="scss" scoped>
.main-store-details {
  padding: 10px 0;

  /* Top Hero Bar */
  .store-hero-card {
    background: #ffffff;
    border-radius: 20px;
    padding: 20px 24px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.03);
    overflow: visible !important;

    .store-id-badge {
      font-size: 16px;
      color: #475569;
      display: inline-flex;
      align-items: center;

      .store-num {
        font-size: 19px;
        color: var(--mainColor-2, #783d73);
        margin-right: 4px;
      }
    }

    .custom-hero-btn {
      font-weight: 700;
      font-size: 13px;
      height: 42px !important;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08) !important;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12) !important;
      }
    }
  }

  /* Modern Card Container */
  .modern-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.03);
    overflow: hidden;

    .card-header-sm {
      padding: 16px 20px;
      background: linear-gradient(
        135deg,
        rgba(120, 61, 115, 0.04) 0%,
        rgba(120, 61, 115, 0.01) 100%
      );
      border-bottom: 1px solid rgba(0, 0, 0, 0.04);

      .title {
        font-size: 15px;
        font-weight: 700;
        color: #1e293b;
      }
    }
  }

  /* Store Sidebar */
  .store-sidebar-card {
    .store_image_container {
      .avatar-glow-ring {
        width: 110px;
        height: 110px;
        margin: 0 auto;
        border-radius: 24px;
        overflow: hidden;
        box-shadow: 0 8px 24px rgba(120, 61, 115, 0.18);
        border: 3px solid #ffffff;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }

    .store-category-chip {
      background: rgba(245, 158, 11, 0.1);
      color: #f59e0b;
      font-size: 12px;
      font-weight: 700;
      padding: 6px 16px;
      border-radius: 20px;
      display: inline-block;
    }

    .website-link-pill {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 18px;
      border-radius: 20px;
      background: linear-gradient(135deg, var(--mainColor-2, #783d73), #9b4f95);
      color: #ffffff;
      text-decoration: none;
      font-size: 12px;
      font-weight: 700;
      box-shadow: 0 4px 12px rgba(120, 61, 115, 0.2);
      transition: transform 0.2s ease;

      &:hover {
        transform: translateY(-2px);
      }
    }

    .store_taps_container {
      list-style: none;
      padding: 0;
      margin: 0;

      .store_tap {
        margin-bottom: 8px;

        button {
          width: 100%;
          display: flex;
          align-items: center;
          padding: 12px 16px;
          border-radius: 14px;
          border: 1px solid rgba(0, 0, 0, 0.04);
          background: #f8fafc;
          color: #64748b;
          font-weight: 700;
          font-size: 14px;
          transition: all 0.25s ease;
          cursor: pointer;

          &:hover {
            background: rgba(120, 61, 115, 0.05);
            color: #783d73;
          }

          &.active {
            background: linear-gradient(
              135deg,
              var(--mainColor-2, #783d73),
              #9b4f95
            );
            color: #ffffff;
            box-shadow: 0 4px 14px rgba(120, 61, 115, 0.25);
            border-color: transparent;
          }
        }
      }
    }

    .switches-box {
      background: #f8fafc !important;
      border: 1px solid rgba(0, 0, 0, 0.05);

      .custom-switch-row {
        cursor: pointer;
        transition: background-color 0.2s ease, transform 0.15s ease;

        &:hover {
          background-color: rgba(120, 61, 115, 0.07) !important;
        }

        &:active {
          transform: scale(0.99);
        }
      }

      ::v-deep .v-input--switch {
        .v-input--selection-controls__input {
          direction: ltr !important;
        }

        .v-input--switch__track {
          left: 2px !important;
          right: auto !important;
          width: 32px !important;
          height: 14px !important;
          top: calc(50% - 7px) !important;
          border-radius: 999px !important;
        }

        .v-input--switch__thumb {
          width: 18px !important;
          height: 18px !important;
          top: calc(50% - 9px) !important;
          left: 0 !important;
          right: auto !important;
          border-radius: 50% !important;
          transform: translate(0, 0) !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
            background-color 0.25s !important;
        }

        &.v-input--is-dirty {
          .v-input--switch__thumb {
            transform: translate(16px, 0) !important;
          }
        }
      }
    }

    .wallet-widget-card {
      background: linear-gradient(135deg, var(--mainColor-2, #783d73), #9b4f95);
      box-shadow: 0 8px 24px rgba(120, 61, 115, 0.25);

      .wallet-action-btn {
        background: rgba(255, 255, 255, 0.2);
        color: #ffffff;
        border: none;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        cursor: pointer;
        transition: transform 0.2s ease, background 0.2s ease;

        &:hover {
          transform: scale(1.15);
          background: #ffffff;
          color: #783d73;
        }
      }
    }
  }

  /* Gallery Image Cards */
  .gallery-image-box {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    height: 220px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

    .image-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(120, 61, 115, 0.4);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      img {
        transform: scale(1.05);
      }
      .image-overlay {
        opacity: 1;
      }
    }
  }

  /* Dialogs */
  .modern-dialog-card {
    border-radius: 24px !important;

    .custom-input-field,
    .custom-textarea-field {
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      padding: 12px 14px;
      font-size: 14px;

      &:focus {
        border-color: #783d73;
        box-shadow: 0 0 0 3px rgba(120, 61, 115, 0.15);
      }
    }
  }

  /* Qualification Status Dot */
  .qualification-status-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 6px 14px;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.04);
    transition: background 0.3s;

    &:hover {
      background: rgba(0, 0, 0, 0.08);
    }
  }

  .status-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;

    &--green {
      background: #2ecc71;
      box-shadow: 0 0 0 3px rgba(46, 204, 113, 0.25);
      animation: pulse-green 2s infinite;
    }

    &--gray {
      background: #aaa;
    }
  }

  .status-dot-label {
    font-size: 12px;
    font-weight: 700;
    color: #475569;
  }

  @keyframes pulse-green {
    0% {
      box-shadow: 0 0 0 0 rgba(46, 204, 113, 0.5);
    }
    70% {
      box-shadow: 0 0 0 7px rgba(46, 204, 113, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(46, 204, 113, 0);
    }
  }

  /* Terms Tooltip */
  .terms-tooltip {
    position: absolute;
    top: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    width: 310px;
    background: #ffffff;
    border: 1px solid rgba(120, 61, 115, 0.15);
    border-radius: 16px;
    padding: 14px;
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    text-align: right;

    &::before {
      content: "";
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 7px solid transparent;
      border-bottom-color: #ffffff;
    }

    &__header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      font-weight: 700;
      font-size: 13px;
      color: var(--mainColor-2, #783d73);
    }

    &__list {
      list-style: none;
      padding: 0;
      margin: 0;
      max-height: 200px;
      overflow-y: auto;

      p {
        margin-bottom: 0;
      }
    }

    &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 6px 8px;
      border-radius: 8px;
      font-size: 12px;
      color: #444;
      background: rgba(0, 0, 0, 0.03);
      margin-bottom: 4px;
    }

    &__index {
      min-width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--mainColor-2, #783d73);
      color: white;
      font-size: 11px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  /* Terms Pop Transition */
  .terms-pop-enter-active,
  .terms-pop-leave-active {
    transition: opacity 0.25s ease, transform 0.25s ease;
  }
  .terms-pop-enter,
  .terms-pop-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(8px);
  }

  .gap-2 {
    gap: 8px;
  }
  .gap-3 {
    gap: 14px;
  }

  /* STORE GALLERY STYLING */
  .store_gallery_wrapper {
    background: #ffffff;
    border-radius: 24px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.04);

    .gallery_header {
      border-bottom: 1px solid #f1f5f9;
      padding-bottom: 16px;

      .header_icon_box {
        width: 48px;
        height: 48px;
        border-radius: 16px;
        background: rgba(120, 61, 115, 0.1);
        color: var(--mainColor-2, #783d73);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.4rem;
      }

      .text_small {
        font-size: 0.825rem;
      }
    }

    .creative_gallery_card {
      background: #ffffff;
      border-radius: 20px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      overflow: hidden;
      box-shadow: 0 8px 20px -5px rgba(15, 23, 42, 0.04);
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 15px 30px -5px rgba(15, 23, 42, 0.08);
        border-color: rgba(120, 61, 115, 0.3);
      }

      .card_tag_header {
        padding: 14px 18px;
        background: #f8fafc;
        border-bottom: 1px solid #f1f5f9;
        font-weight: 700;
        font-size: 0.9rem;
        color: #334155;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          color: var(--mainColor-2, #783d73);
          font-size: 1.05rem;
        }
      }

      .gallery_img_container {
        position: relative !important;
        height: 220px !important;
        width: 100% !important;
        background: #f1f5f9;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        overflow: hidden !important;

        .gallery_img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          display: block !important;
          transition: transform 0.4s ease;
        }

        .zoom_hover_overlay {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100% !important;
          height: 100% !important;
          z-index: 5 !important;
          background: rgba(15, 23, 42, 0.72) !important;
          backdrop-filter: blur(4px) !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          color: #ffffff !important;
          opacity: 0;
          transition: opacity 0.3s ease;
          font-size: 0.875rem;
          font-weight: 700;

          .zoom_icon_box {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.3rem;
          }
        }

        &:hover {
          .gallery_img {
            transform: scale(1.08);
          }

          .zoom_hover_overlay {
            opacity: 1 !important;
          }
        }

        .empty_image_placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 500;

          i {
            font-size: 2.2rem;
          }
        }
      }
    }
  }

  .preview_img_box {
    max-height: 480px;
    overflow: hidden;
    border-radius: 18px;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;

    .full_preview_img {
      max-width: 100%;
      max-height: 480px;
      object-fit: contain;
      border-radius: 18px;
    }
  }
}

/* CREATIVE LIGHTBOX MODAL STYLING */
::v-deep .creative_lightbox_dialog_overlay {
  border-radius: 28px !important;
  overflow: hidden !important;
  box-shadow: 0 30px 70px -15px rgba(15, 23, 42, 0.45) !important;
}

.lightbox_card_wrapper {
  border-radius: 28px !important;
  background: #ffffff !important;
  position: relative;
  overflow: hidden;

  .lightbox_close_btn {
    position: absolute;
    top: 20px;
    left: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #f1f5f9;
    color: #64748b;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 10;

    &:hover {
      background: #0f172a;
      color: #ffffff;
      transform: rotate(90deg) scale(1.08);
    }
  }

  .lightbox_header {
    .header_badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 16px;
      border-radius: 50px;
      background: rgba(120, 61, 115, 0.08);
      color: var(--mainColor-2, #783d73);
      font-size: 0.825rem;
      font-weight: 700;
    }

    .lightbox_title {
      font-weight: 800;
      color: #0f172a;
      font-size: 1.35rem;
    }
  }

  .lightbox_stage {
    position: relative;
    max-height: 520px;
    border-radius: 20px;
    background: #0f172a;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow: hidden;
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.4);

    .stage_glow_bg {
      position: absolute;
      inset: 0;
      background: radial-gradient(
        circle at center,
        rgba(120, 61, 115, 0.25) 0%,
        transparent 70%
      );
      pointer-events: none;
    }

    .lightbox_image {
      max-width: 100%;
      max-height: 480px;
      object-fit: contain;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.02);
      }
    }
  }

  .lightbox_footer_actions {
    .btn_lightbox_primary {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 24px;
      border-radius: 50px;
      background: linear-gradient(
        135deg,
        var(--mainColor-2, #783d73) 0%,
        #9b4f95 100%
      );
      color: #ffffff !important;
      font-weight: 700;
      font-size: 0.9rem;
      text-decoration: none;
      box-shadow: 0 10px 20px -5px rgba(120, 61, 115, 0.35);
      transition: all 0.25s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 14px 25px -5px rgba(120, 61, 115, 0.45);
      }
    }

    .btn_lightbox_secondary {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 24px;
      border-radius: 50px;
      background: #f1f5f9;
      color: #334155 !important;
      font-weight: 700;
      font-size: 0.9rem;
      text-decoration: none;
      border: 1px solid #e2e8f0;
      transition: all 0.25s ease;

      &:hover {
        background: #e2e8f0;
        color: #0f172a !important;
        transform: translateY(-2px);
      }
    }

    .btn_lightbox_close {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 24px;
      border-radius: 50px;
      background: transparent;
      color: #64748b;
      font-weight: 700;
      font-size: 0.9rem;
      border: 2px solid #cbd5e1;
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover {
        background: #f8fafc;
        border-color: #94a3b8;
        color: #334155;
      }
    }
  }
}
</style>
